import React, { useState, useEffect } from "react";
import { Box, Typography, TextField, InputAdornment, Grid } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddProductFamilyForm from "./AddProductFamilyForm";
import ProductFamilyCard from "./Permissions/ProductFamilyCard";
import EditProductFamilyDialog from "./Permissions/EditProductFamilyDialog";
import translations from "../translations/productFamilyTranslation";
import { isTestEnv } from "../globals";

const ProductFamily = ({ rolesList, setMockDropdownData, setSnackbar, execute }) => {
    const [productFamilies, setProductFamilies] = useState([]);
    const [editProductFamilyId, setEditProductFamilyId] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");

    useEffect(() => {
        const getData = async () => {
            try {
                const familyResponse = await execute(
                    "GET",
                    `${process.env.REACT_APP_BACKEND_BASE_URL}/api/productFamily/getinfrastructuresProductFamilys`
                );
                setProductFamilies(familyResponse.data);
            } catch (err) {
                isTestEnv && console.error(34,err);
            }
        };
        getData();
    }, []);

    const handleDeleteProductFamily = async (pfId) => {
        const isSure = window.confirm(translations.deleteProductFamilyConfirm);
        if (isSure) {
            try {
                await execute(
                    "PATCH",
                    `${process.env.REACT_APP_BACKEND_BASE_URL}/api/productFamily/updateProductFamily/`,
                    { _id: pfId, infrastructures: false, orderApprovalRequired: false, approverRole: null }
                );
                setProductFamilies(productFamilies.filter((pf) => pf._id !== pfId));
                setMockDropdownData((prev) => ({ ...prev, family: productFamilies.filter((pf) => pf._id !== pfId) }));
                setSnackbar({
                    open: true,
                    message: translations.productFamilyDeletedSuccess,
                    severity: 'success'
                });
            } catch (error) {
                isTestEnv && console.error(error);
                setSnackbar({
                    open: true,
                    message: translations.error,
                    severity: 'error'
                });
            }
        }
    };

    const handleSave = async (updatedProductFamily) => {
        updatedProductFamily = {
            ...updatedProductFamily,
            approverRole: updatedProductFamily.orderApprovalRequired ? updatedProductFamily.approverRole : null
        };
        try {
            await execute(
                "PATCH",
                `${process.env.REACT_APP_BACKEND_BASE_URL}/api/productFamily/updateProductFamily/`,
                { ...updatedProductFamily }
            );
            setProductFamilies(prevFamilies =>
                prevFamilies.map(pf =>
                    pf._id === updatedProductFamily._id ? updatedProductFamily : pf
                )
            );
            setSnackbar({
                open: true,
                message: translations.productFamilyUpdateSuccess,
                severity: 'success'
            });
        } catch (err) {
            isTestEnv && console.error(err);
            setSnackbar({
                open: true,
                message: translations.productFamilyUpdateError,
                severity: 'error'
            });
        }
        setEditProductFamilyId(null);
    };

    const filteredFamilies = productFamilies.filter((pf) => {
        if (!searchTerm.trim()) return true;
        const term = searchTerm.toLowerCase();
        return pf.familyName?.toLowerCase().includes(term) || pf.familyDes?.toLowerCase().includes(term);
    });

    return (
        <Box mt={2}>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 3,
                }}
            >
                <Typography variant="subtitle1">
                    {translations.productFamilies}
                </Typography>
                <AddProductFamilyForm
                    rolesList={rolesList}
                    setProductFamilies={setProductFamilies}
                    setMockDropdownData={setMockDropdownData}
                />
            </Box>

            <Box sx={{ mb: 3 }}>
                <TextField
                    placeholder="חיפוש משפחת מוצר..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    variant="outlined"
                    size="small"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ minWidth: 300 }}
                />
            </Box>

            <Grid container spacing={3}>
                {filteredFamilies.map((pf) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={pf._id}>
                        <ProductFamilyCard pf={pf} onEdit={(p) => setEditProductFamilyId(p._id)} onDelete={handleDeleteProductFamily} />
                    </Grid>
                ))}
            </Grid>

            <EditProductFamilyDialog
                pf={productFamilies.find((pf) => pf._id === editProductFamilyId) || null}
                open={!!editProductFamilyId}
                onClose={() => setEditProductFamilyId(null)}
                rolesList={rolesList}
                onSave={handleSave}
            />
        </Box>
    );
};

export default ProductFamily;
